import { Link } from "react-router-dom";
import { useEffect } from "react";
import { toast } from "sonner";
import "../bingo.css";
import {
  ArrowLeft,
  Monitor,
  Volume2,
  VolumeX,
  Maximize2,
  Settings as SettingsIcon,
} from "lucide-react";

import {
  GameStatus,
  useBingoGameStore,
} from "@/plugins/bingo/state/bingoGameStore";
import { useSettingsStore } from "@/core/state/settingsStore";
import { attachHostSyncBridge } from "@/plugins/bingo/state/hostSyncBridge";
import { audioService } from "@/core/audio/audioService";

import GameLifecycleControls from "./GameLifecycleControls";
import CallingControls from "./CallingControls";
import UndoButton from "./UndoButton";
import StageSelector from "./StageSelector";
import WinnerPanel from "./WinnerPanel";
import BingoCheckPanel from "./BingoCheckPanel";
import BoardMirror from "./BoardMirror";
import CurrentPreviousMirror from "./CurrentPreviousMirror";
import CalledHistory from "./CalledHistory";
import { useAutoMode } from "./useAutoMode";

const STATUS_LABEL = {
  IDLE: "Idle",
  PRE_GAME_TRANSITION: "Starting...",
  IN_PROGRESS: "Live",
  PAUSED: "Paused",
  REVIEWING: "Review",
  ANNOUNCING_WINNER: "Winner",
  GAME_SUMMARY: "Ended",
};

const STATUS_COLOR = {
  IDLE: "var(--ic-text-muted)",
  PRE_GAME_TRANSITION: "var(--ic-info)",
  IN_PROGRESS: "var(--ic-live)",
  PAUSED: "var(--ic-info)",
  REVIEWING: "var(--ic-info)",
  ANNOUNCING_WINNER: "var(--ic-primary)",
  GAME_SUMMARY: "var(--ic-text-muted)",
};

const STAGE_LABEL = {
  one_line: "1 Line",
  two_lines: "2 Lines",
  full_house: "Full House",
};

/**
 * HostPanel — the pro broadcast console for the animator.
 * Composes: top bar + 3-column control room (25% / 50% / 25%).
 */
export default function HostPanel() {
  const status = useBingoGameStore((s) => s.status);
  const stage = useBingoGameStore((s) => s.stage);
  const error = useBingoGameStore((s) => s.error);
  const muted = useSettingsStore((s) => s.settings.muted);
  const toggleMute = useSettingsStore((s) => s.toggleMute);

  useAutoMode();

  useEffect(() => {
    const detach = attachHostSyncBridge();
    audioService.unlockOnUserGesture();
    return detach;
  }, []);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const openProjector = () => {
    window.open(
      "/projector/bingo",
      "ic-bingo-projector",
      "noopener,width=1600,height=900",
    );
  };

  return (
    <div
      data-testid="host-panel"
      data-bingo-shell
      className="ic-app ic-bingo-shell min-h-screen w-full flex flex-col"
    >
      {/* Top bar */}
      <header
        data-testid="host-topbar"
        className="flex items-center justify-between px-6 py-3 border-b border-[color:var(--ic-border-subtle)] bg-[color:var(--ic-bg-surface)]"
      >
        <div className="flex items-center gap-4">
          <Link
            data-testid="host-back"
            to="/admin"
            className="inline-flex items-center gap-2 text-sm text-[color:var(--ic-text-secondary)] hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>
          <div className="w-px h-6 bg-[color:var(--ic-border-subtle)]" />
          <div className="ic-font-display font-semibold tracking-tight">
            Bingo Show
            <span className="text-[color:var(--ic-text-muted)] text-xs tracking-[0.3em] uppercase ml-2">
              Host Panel
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span
            data-testid="host-stage-badge"
            className="text-[10px] tracking-[0.3em] uppercase px-2 py-1 rounded border border-[color:var(--ic-border-subtle)] text-[color:var(--ic-text-secondary)]"
          >
            {STAGE_LABEL[stage]}
          </span>
          <span
            data-testid="host-status-badge"
            className="text-[10px] tracking-[0.3em] uppercase px-2 py-1 rounded border inline-flex items-center gap-2"
            style={{
              borderColor: "var(--ic-border-subtle)",
              color: STATUS_COLOR[status],
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: STATUS_COLOR[status],
                boxShadow:
                  status === GameStatus.IN_PROGRESS
                    ? "0 0 8px var(--ic-live)"
                    : "none",
              }}
            />
            {STATUS_LABEL[status]}
          </span>
          <button
            data-testid="host-mute-toggle"
            type="button"
            onClick={toggleMute}
            className="w-9 h-9 rounded-md flex items-center justify-center hover:bg-[color:var(--ic-bg-elevated)]"
            aria-pressed={muted}
          >
            {muted ? (
              <VolumeX className="w-4 h-4" />
            ) : (
              <Volume2 className="w-4 h-4" />
            )}
          </button>
          <Link
            data-testid="host-settings-link"
            to="/host/bingo/settings"
            className="w-9 h-9 rounded-md flex items-center justify-center hover:bg-[color:var(--ic-bg-elevated)]"
            aria-label="Settings"
          >
            <SettingsIcon className="w-4 h-4" />
          </Link>
          <button
            data-testid="host-open-projector"
            type="button"
            onClick={openProjector}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm border border-[color:var(--ic-border-subtle)] hover:border-[color:var(--ic-border-focus)]"
          >
            <Monitor className="w-4 h-4" />
            Open Projector
          </button>
          <button
            data-testid="host-fullscreen"
            type="button"
            className="w-9 h-9 rounded-md flex items-center justify-center hover:bg-[color:var(--ic-bg-elevated)]"
            onClick={() => {
              if (document.fullscreenElement) {
                document.exitFullscreen?.();
              } else {
                document.documentElement.requestFullscreen?.();
              }
            }}
            aria-label="Toggle fullscreen"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* 3-column control room — adaptive: 3/6/3 at ≥1280px, 4/8 with
          history moved to the bottom at 900–1279px. */}
      <main
        data-testid="host-grid"
        className="flex-1 grid grid-cols-12 gap-3 p-3 min-h-0"
      >
        {/* Column 1 — Controls */}
        <section
          data-testid="host-col-controls"
          className="col-span-12 md:col-span-4 xl:col-span-3 rounded-xl border border-[color:var(--ic-border-subtle)] bg-[color:var(--ic-bg-surface)] p-4 flex flex-col gap-4 min-h-0 overflow-y-auto"
        >
          <ColHeading>Lifecycle</ColHeading>
          <GameLifecycleControls />

          <div className="h-px bg-[color:var(--ic-border-subtle)] my-1" />

          <ColHeading>Calling</ColHeading>
          <CallingControls />

          <div className="h-px bg-[color:var(--ic-border-subtle)] my-1" />

          <ColHeading>Correction</ColHeading>
          <UndoButton />

          <div className="h-px bg-[color:var(--ic-border-subtle)] my-1" />

          <StageSelector />
        </section>

        {/* Column 2 — Board Mirror */}
        <section
          data-testid="host-col-board"
          className="col-span-12 md:col-span-8 xl:col-span-6 rounded-xl border border-[color:var(--ic-border-subtle)] bg-[color:var(--ic-bg-surface)] p-4 flex flex-col min-h-0"
        >
          <CurrentPreviousMirror />
          <div className="flex-1 min-h-0">
            <BoardMirror />
          </div>
        </section>

        {/* Column 3 — History & Winner */}
        <section
          data-testid="host-col-history"
          className="col-span-12 xl:col-span-3 rounded-xl border border-[color:var(--ic-border-subtle)] bg-[color:var(--ic-bg-surface)] p-4 flex flex-col gap-4 min-h-0 overflow-y-auto"
        >
          <BingoCheckPanel />
          <div className="h-px bg-[color:var(--ic-border-subtle)]" />
          <WinnerPanel />
          <div className="h-px bg-[color:var(--ic-border-subtle)]" />
          <CalledHistory />
        </section>
      </main>
    </div>
  );
}

function ColHeading({ children }) {
  return (
    <h2 className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--ic-text-muted)]">
      {children}
    </h2>
  );
}
